<template>
  <v-navigation-drawer
    :width="320 + sidePanelWidth"
    location="end"
    temporary
    :model-value="!!selectedTransaction"
    @update:model-value="closeTransactionDrawer()"
  >
    <div v-if="selectedTransaction && account" class="h-full flex justify-end">
      <FilePreviewPanel
        v-if="mdAndUp && previewFileId"
        v-model:file-id="previewFileId"
        class="border-e border-gray-300 grow min-w-0"
        :files="files"
        :get-url="getFileUrl"
      />
      <CommentChat
        v-else-if="mdAndUp && commentsOpen"
        class="border-e border-gray-300 grow min-w-0"
        :account-id="accountId"
        :account="account"
        :targets="commentTargets"
        :comments="comments"
        :unread-count="unreadCount(commentTargets)"
        @close="commentsOpen = false"
      />
      <div class="w-[320px] shrink-0 h-full overflow-y-auto flex flex-col">
        <v-toolbar density="compact" color="surface">
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeTransactionDrawer()"
          ></v-btn>
        </v-toolbar>
        <div class="px-4 pb-4 flex flex-col items-start">
          <div class="text-xs text-medium-emphasis">{{ selectedTransaction.name }}</div>
          <div class="text-3xl mt-2">{{ selectedTransaction.amountFormatted }}</div>
        </div>
        <v-divider class="mx-4" />

        <v-list v-if="details.length || files.length || hasCategories">
          <v-list-item
            v-for="detail in details"
            :key="detail.key"
            :to="detail.to"
            :append-icon="detail.to ? 'mdi-chevron-right' : undefined"
          >
            <v-list-item-title>{{ detail.title }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ detail.value }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item v-if="hasCategories && !user.hasRole(supervisorRoles, { account })">
            <v-list-item-title>{{ t("common.category") }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ readOnlyCategoryLabel }}
            </v-list-item-subtitle>
          </v-list-item>
          <div v-if="files.length" class="px-4 py-2 flex flex-col gap-2">
            <div class="text-sm">{{ t("common.files") }}</div>
            <TransferFileCard
              v-for="file in files"
              :key="file.id"
              :file="file"
              :active="file.id === previewFileId"
              :get-url="(disposition) => getFileUrl(file, disposition)"
              @preview="togglePreview(file.id)"
            />
          </div>
        </v-list>
        <div
          v-if="hasCategories && user.hasRole(supervisorRoles, { account })"
          class="mx-3 mt-2 mb-3"
        >
          <div v-if="assignment.mode === 'split'" class="flex items-center gap-2">
            <div class="grow">
              <div class="text-xs text-medium-emphasis mb-1">{{ t("common.category") }}</div>
              <div class="flex flex-wrap gap-1">
                <v-chip
                  v-for="(item, index) in splitCategoryItems"
                  :key="`${item.id}-${index}`"
                  color="primary"
                  size="small"
                  label
                  variant="outlined"
                >
                  {{ item.name }}
                </v-chip>
              </div>
            </div>
            <v-btn
              v-if="canEditSplits"
              v-tooltip="t('transactions.split.edit')"
              icon="mdi-call-split"
              variant="text"
              size="small"
              :aria-label="t('transactions.split.edit')"
              @click="openSplitForSelected"
            />
          </div>
          <div v-else-if="suggestedCategoryName" class="flex flex-col gap-2">
            <div class="text-xs text-medium-emphasis">{{ t("common.category") }}</div>
            <v-chip color="secondary" label class="self-start">
              <v-icon
                v-tooltip="t('transactions.suggestedCategory.tooltip')"
                start
                icon="mdi-star-four-points-outline"
                size="small"
              />
              {{ suggestedCategoryName }}
              <span class="inline-flex items-center gap-2 ms-2 me-1">
                <v-btn
                  v-tooltip="t('transactions.suggestedCategory.accept')"
                  icon="mdi-check"
                  variant="text"
                  density="compact"
                  size="small"
                  :style="{ '--v-hover-opacity': 0.14 }"
                  :aria-label="t('transactions.suggestedCategory.accept')"
                  @click="onAcceptSuggestion"
                />
                <v-btn
                  v-tooltip="t('transactions.suggestedCategory.reject')"
                  icon="mdi-close"
                  variant="text"
                  density="compact"
                  size="small"
                  :style="{ '--v-hover-opacity': 0.14 }"
                  :aria-label="t('transactions.suggestedCategory.reject')"
                  @click="onRejectSuggestion"
                />
              </span>
            </v-chip>
            <div class="flex items-center gap-2">
              <v-autocomplete
                v-model="selectedCategoryId"
                :items="selectorItems"
                item-title="name"
                item-value="id"
                :item-props="categoryItemProps"
                :label="t('common.category')"
                :placeholder="t('transfers.selectCategory')"
                variant="outlined"
                density="compact"
                clearable
                hide-details
                class="grow"
                @update:model-value="updateCategory"
              />
              <v-btn
                v-if="canEditSplits"
                v-tooltip="t('transactions.split.open')"
                icon="mdi-call-split"
                variant="text"
                size="small"
                :aria-label="t('transactions.split.open')"
                @click="openSplitForSelected"
              />
            </div>
          </div>
          <div v-else class="flex items-center gap-2">
            <v-autocomplete
              v-model="selectedCategoryId"
              :items="selectorItems"
              item-title="name"
              item-value="id"
              :item-props="categoryItemProps"
              :label="t('common.category')"
              :placeholder="t('transfers.selectCategory')"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              class="grow"
              @update:model-value="updateCategory"
            />
            <v-btn
              v-if="canEditSplits"
              v-tooltip="t('transactions.split.open')"
              icon="mdi-call-split"
              variant="text"
              size="small"
              :aria-label="t('transactions.split.open')"
              @click="openSplitForSelected"
            />
          </div>
        </div>
        <CommentsListItem
          v-if="getProviderType(account.accountType) === 'proAccount'"
          :comments="comments"
          :unread-count="unreadCount(commentTargets)"
          :active="commentsOpen"
          @click="toggleChat()"
        />
        <AdminOnlyCard
          v-if="
            allowTreezorSimulation &&
            account.accountType === 'treezor' &&
            selectedTransaction.is_future
          "
          class="m-3 mt-auto"
        >
          <v-btn
            :loading="validatingTreezorPayout"
            :disabled="validatingTreezorPayout"
            color="primary"
            variant="outlined"
            append-icon="mdi-cash-check"
            @click="simulateTreezorPayoutValidation()"
          >
            {{ t("transactions.details.simulateTreezorPayoutValidation") }}
          </v-btn>
        </AdminOnlyCard>
      </div>
    </div>
    <v-dialog
      v-if="!mdAndUp && selectedTransaction"
      :model-value="!!previewFileId"
      fullscreen
      @update:model-value="previewFileId = null"
    >
      <v-card>
        <FilePreviewPanel v-model:file-id="previewFileId" :files="files" :get-url="getFileUrl" />
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="!mdAndUp && selectedTransaction"
      :model-value="commentsOpen"
      fullscreen
      @update:model-value="commentsOpen = false"
    >
      <v-card>
        <CommentChat
          v-if="commentsOpen"
          :account-id="accountId"
          :account="account"
          :targets="commentTargets"
          :comments="comments"
          :unread-count="unreadCount(commentTargets)"
          @close="commentsOpen = false"
        />
      </v-card>
    </v-dialog>
  </v-navigation-drawer>

  <SplitCategoriesDialog
    v-if="canEditSplits"
    v-model="showSplitDialog"
    :transaction="splitTargetTransaction"
    :selector-items="splitDialogSelectorItems"
    :category-item-props="categoryItemProps"
    :resolve-category-name="resolveTrackedCategoryName"
    :active-category-ids="activeCategoryIds"
    :loading="savingSplits"
    @save="onSaveSplits"
    @save-single="onSaveSingleFromSplit"
  />
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import CommentChat from "@/components/CommentChat.vue";
import CommentsListItem from "@/components/CommentsListItem.vue";
import FilePreviewPanel from "@/components/FilePreviewPanel.vue";
import TransferFileCard from "@/components/TransferFileCard.vue";
import { useBankAccount } from "@/composables/useBankAccount";
import { useComments, useUnreadComments } from "@/composables/useComments";
import { useI18n } from "@/composables/useI18n";
import { useTransactionCategories } from "@/composables/useTransactionCategories";
import { call } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { useSelectedTransactionStore } from "@/store/selectedTransaction";
import { transactionCommentTargets } from "@/utils/comments";
import type { FileUrlDisposition } from "@/utils/files";
import { formatTrackedCategoryNames } from "@/utils/trackedCategoryLabels";
import type { TransactionFileWithSource } from "@/utils/types";
import { getProviderType } from "@aircap/common/account";
import { supervisorRoles } from "@aircap/common/auth";
import {
  getTrackedCategoryAssignment,
  type TrackedCategorySplit,
} from "@aircap/common/trackedCategory";
import { captureException } from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, toRef, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { useDisplay } from "vuetify";
import SplitCategoriesDialog from "./SplitCategoriesDialog.vue";

const props = withDefaults(
  defineProps<{
    accountId: string;
    showBeneficiaryButton?: boolean;
  }>(),
  {
    showBeneficiaryButton: true,
  },
);

const accountId = toRef(props, "accountId");
const { account } = useBankAccount(accountId);

const selectedTransactionStore = useSelectedTransactionStore();
const { selectedTransaction, splitTargetTransaction, commentsOpen } =
  storeToRefs(selectedTransactionStore);
const { closeTransactionDrawer, openSplitDialog, closeSplitDialog, patchTransaction } =
  selectedTransactionStore;

const { showErrorToast, showSuccessToast } = useToast();
const { t } = useI18n();
const user = useUserStore();

const validatingTreezorPayout = ref(false);
const allowTreezorSimulation = import.meta.env.VITE_TREEZOR_ALLOW_SIMULATION === "true";

const {
  hasTrackedCategories,
  canSplitCategories,
  categoriesList,
  getSelectorItems,
  categoryItemProps,
  setCategory,
  setCategorySplits,
  acceptSuggestedTrackedCategory,
  rejectSuggestedTrackedCategory,
  resolveTrackedCategoryName,
  resolveActiveTrackedCategoryName,
  trackedCategories,
} = useTransactionCategories(accountId);

const activeCategoryIds = computed(() => trackedCategories.value.map((c) => c.id));

const selectedCategoryId = ref<string | null>(null);
const savingSplits = ref(false);

const showSplitDialog = computed({
  get: () => !!splitTargetTransaction.value,
  set: (open: boolean) => {
    if (!open) closeSplitDialog();
  },
});

const hasCategories = computed(() => categoriesList.value.length > 0);

const selectorItems = computed(() => getSelectorItems(selectedTransaction.value?.amount));
const splitDialogSelectorItems = computed(() =>
  getSelectorItems(splitTargetTransaction.value?.amount),
);

const canEditSplits = canSplitCategories;

const openSplitForSelected = () => {
  if (!selectedTransaction.value) return;
  openSplitDialog(selectedTransaction.value);
};

watch(accountId, () => {
  closeSplitDialog();
  closeTransactionDrawer();
});
onBeforeUnmount(() => {
  closeSplitDialog();
  closeTransactionDrawer();
});

const assignment = computed(() =>
  getTrackedCategoryAssignment(selectedTransaction.value ?? { amount: 0 }),
);

const splitCategoryItems = computed(() => {
  if (assignment.value.mode !== "split") return [];
  return assignment.value.splits.map((split) => ({
    id: split.trackedCategoryId,
    name: resolveTrackedCategoryName(split.trackedCategoryId) ?? split.trackedCategoryId,
  }));
});

const readOnlyCategoryLabel = computed(() => {
  if (!selectedTransaction.value) return "-";
  if (hasTrackedCategories.value) {
    return formatTrackedCategoryNames(assignment.value, resolveTrackedCategoryName) || "-";
  }
  return categoriesList.value.find((c) => c.id === selectedCategoryId.value)?.name || "-";
});

const suggestedCategoryName = computed(() => {
  if (!selectedTransaction.value || !hasTrackedCategories.value) return null;
  if (assignment.value.mode !== "none") return null;
  const suggestedId = selectedTransaction.value.suggestedTrackedCategoryId;
  if (!suggestedId) return null;
  return resolveActiveTrackedCategoryName(suggestedId) ?? null;
});

watch(
  [() => selectedTransaction.value, hasTrackedCategories, assignment, categoriesList] as const,
  ([transaction, trackedEnabled, currentAssignment, availableCategories]) => {
    if (!transaction) {
      selectedCategoryId.value = null;
      return;
    }

    if (trackedEnabled) {
      switch (currentAssignment.mode) {
        case "split":
        case "none":
          selectedCategoryId.value = null;
          return;
        case "single": {
          // Keep the id even while categories are still loading so the control can bind
          // once items arrive. Clear only when the loaded list proves the category is gone.
          if (
            availableCategories.length > 0 &&
            !availableCategories.some((c) => c.id === currentAssignment.trackedCategoryId)
          ) {
            selectedCategoryId.value = null;
            return;
          }
          selectedCategoryId.value = currentAssignment.trackedCategoryId;
          return;
        }
      }
    }

    selectedCategoryId.value = transaction.category?.id ?? null;
  },
  { immediate: true },
);

const files = computed(() => {
  return selectedTransaction.value?.files ?? [];
});

const previewFileId = ref<string | null>(null);
watch(
  () => selectedTransaction.value?.id,
  () => {
    previewFileId.value = null;
  },
);

const togglePreview = (fileId: string) => {
  previewFileId.value = previewFileId.value === fileId ? null : fileId;
  commentsOpen.value = false;
};
const toggleChat = () => {
  commentsOpen.value = !commentsOpen.value;
  previewFileId.value = null;
};

const commentTargets = computed(() => {
  if (!selectedTransaction.value || !account.value) return [];
  if (getProviderType(account.value.accountType) !== "proAccount") return [];
  return transactionCommentTargets(selectedTransaction.value);
});
const { comments } = useComments(accountId, commentTargets);
const { unreadCount } = useUnreadComments(accountId);

const { mdAndUp, width: displayWidth } = useDisplay();
const sidePanelWidth = computed(() => {
  if (!mdAndUp.value) return 0;
  if (previewFileId.value) return Math.min(900, Math.round(displayWidth.value * 0.6));
  if (commentsOpen.value) return 400;
  return 0;
});

const updateCategory = async (categoryId: string | null) => {
  if (!selectedTransaction.value?.id) return false;

  try {
    console.log("Setting transaction category...");
    await setCategory(selectedTransaction.value.id, categoryId, selectedTransaction.value.amount);
    patchTransaction(selectedTransaction.value.id, {
      trackedCategorySplits: categoryId
        ? [{ trackedCategoryId: categoryId, amount: Math.abs(selectedTransaction.value.amount) }]
        : undefined,
      suggestedTrackedCategoryId: undefined,
    });
    return true;
  } catch (cause) {
    const error = new Error("Error updating category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
    return false;
  }
};

const onAcceptSuggestion = async () => {
  const transaction = selectedTransaction.value;
  const suggestedId = transaction?.suggestedTrackedCategoryId;
  if (!transaction?.id || !suggestedId) return;

  try {
    await acceptSuggestedTrackedCategory(transaction.id, suggestedId, transaction.amount);
    patchTransaction(transaction.id, {
      trackedCategorySplits: [
        { trackedCategoryId: suggestedId, amount: Math.abs(transaction.amount) },
      ],
      suggestedTrackedCategoryId: undefined,
    });
  } catch (cause) {
    const error = new Error("Error accepting suggested category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const onRejectSuggestion = async () => {
  const transaction = selectedTransaction.value;
  const suggestedId = transaction?.suggestedTrackedCategoryId;
  if (!transaction?.id || !suggestedId) return;

  try {
    await rejectSuggestedTrackedCategory(transaction.id, suggestedId);
    patchTransaction(transaction.id, {
      suggestedTrackedCategoryId: undefined,
      rejectedSuggestedTrackedCategoryId: suggestedId,
    });
  } catch (cause) {
    const error = new Error("Error rejecting suggested category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const onSaveSplits = async (splits: TrackedCategorySplit[]) => {
  const target = splitTargetTransaction.value;
  if (!target?.id || savingSplits.value) return;

  savingSplits.value = true;
  try {
    console.log("Setting transaction category splits...");
    await setCategorySplits(target.id, splits, target.amount);
    patchTransaction(target.id, {
      trackedCategorySplits: splits,
      suggestedTrackedCategoryId: undefined,
    });
    if (splitTargetTransaction.value?.id === target.id) {
      closeSplitDialog();
    }
  } catch (cause) {
    const error = new Error("Error updating category splits", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    savingSplits.value = false;
  }
};

const onSaveSingleFromSplit = async (categoryId: string) => {
  const target = splitTargetTransaction.value;
  if (!target?.id || savingSplits.value) return;

  savingSplits.value = true;
  try {
    console.log("Setting transaction category...");
    await setCategory(target.id, categoryId, target.amount);
    patchTransaction(target.id, {
      trackedCategorySplits: [{ trackedCategoryId: categoryId, amount: Math.abs(target.amount) }],
      suggestedTrackedCategoryId: undefined,
    });
    if (selectedTransaction.value?.id === target.id) {
      selectedCategoryId.value = categoryId;
    }
    if (splitTargetTransaction.value?.id === target.id) {
      closeSplitDialog();
    }
  } catch (cause) {
    const error = new Error("Error updating category", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    savingSplits.value = false;
  }
};

const details = computed(() => {
  if (!selectedTransaction.value) return [];

  const { label, reference, counterparty, counterpartyIban, dateFormatted } =
    selectedTransaction.value;

  // Header already shows name + amount. Always surface Libellé when present — for
  // Swan virements it is the remittance wording and must stay explicitly labeled,
  // even when it currently equals the display name.
  return [
    {
      key: "date",
      title: t("transactions.details.date"),
      value: dateFormatted.long,
    },
    ...(counterparty
      ? [
          {
            key: "counterparty",
            title: t("transactions.details.counterparty"),
            value: counterparty,
          },
        ]
      : []),
    ...(counterpartyIban
      ? [
          {
            key: "counterpartyIban",
            title: t("transactions.details.counterpartyIban"),
            value: counterpartyIban,
            to: props.showBeneficiaryButton
              ? {
                  name: "BeneficiaryDetail",
                  params: {
                    accountId: accountId.value,
                    beneficiaryIban: counterpartyIban,
                  },
                }
              : undefined,
          },
        ]
      : []),
    ...(label
      ? [
          {
            key: "label",
            title: t("transactions.details.label"),
            value: label,
          },
        ]
      : []),
    ...(reference
      ? [
          {
            key: "reference",
            title: t("transactions.details.reference"),
            value: reference,
          },
        ]
      : []),
  ] satisfies { key: string; title: string; value: string; to?: RouteLocationRaw }[];
});

const getFileUrl = async (file: TransactionFileWithSource, disposition: FileUrlDisposition) => {
  if (!selectedTransaction.value) throw new Error("No transaction selected");
  switch (file.source) {
    case "transaction": {
      const response = await call("getTransactionFileUrl")({
        fileId: file.id,
        accountId: props.accountId,
        transactionId: selectedTransaction.value.id,
        disposition,
      });
      return response.url;
    }
    case "transferRequest": {
      if (!selectedTransaction.value.transferRequest) throw new Error("Transfer not found");
      const response = await call("getTransferRequestFileUrl")({
        fileId: file.id,
        accountId: props.accountId,
        transferId: selectedTransaction.value.transferRequest.id,
        disposition,
      });
      return response.url;
    }
  }
};

const simulateTreezorPayoutValidation = async () => {
  const transaction = selectedTransaction.value;
  if (!transaction?.is_future || account.value?.accountType !== "treezor") return;

  validatingTreezorPayout.value = true;
  try {
    console.log("Simulating Treezor payout validation...");
    await call("simulateTreezorPayoutValidation")({
      payoutId: parseInt(transaction.id, 10),
      payoutStatus: "VALIDATED",
    });
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error simulating Treezor payout validation", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    validatingTreezorPayout.value = false;
  }
};
</script>
